import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export type AlertLevel = 'success' | 'warning' | 'danger' | 'info';
const ALERT_LEVELS: Array<AlertLevel> = ['success', 'warning', 'danger', 'info'];

export interface AlertProps {
  level?: AlertLevel;
  dismissible?: boolean;
  onDismiss?: () => unknown;
  children?: React.ReactNode;
  className?: string;
  isSaturated?: boolean;
}

const Alert: React.FC<AlertProps> = (props) => {
  const { level, dismissible, onDismiss, children, className, isSaturated } = props;

  return (
    <div
      data-testid="alert"
      className={classNames(`bem-alert bem-alert-is-${level}`, className, {
        'bem-alert-is-saturated': isSaturated,
        [`bem-alert-is-saturated-is-${level}`]: isSaturated,
      })}
    >
      {dismissible && (
        <button type="button" className="close" aria-label="Close" onClick={onDismiss}>
          <span aria-hidden="true">&times;</span>
        </button>
      )}
      <div>{children}</div>
    </div>
  );
};

Alert.propTypes = {
  /** The severity of the alert. One of 'success' | 'warning' | 'danger' | 'info'. */
  level: PropTypes.oneOf(ALERT_LEVELS),
  /** Whether or not to instead use the highly saturated alerts */
  isSaturated: PropTypes.bool,
  /** Whether or not the user can dismiss (close) the alert. */
  dismissible: PropTypes.bool,
  /** A function to be called when the alert is dismissed. */
  onDismiss: PropTypes.func,
  /** The contents of the alert. */
  children: PropTypes.node,
  /** A class name to apply to the outermost element of the alert. */
  className: PropTypes.string,
};

Alert.defaultProps = {
  level: 'info',
  isSaturated: false,
  dismissible: false,
  onDismiss: () => {},
  children: undefined,
  className: '',
};

export default Alert;
